import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Bell,
  Calendar,
  CalendarCheck,
  ClipboardList,
  FileText,
  Link2,
  MessageSquare,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  UserPlus,
  Zap,
} from "lucide-react";
import { FloatingFeatureBadge } from "@/components/landing/shared/FloatingFeatureBadge";

export type UseCaseBadgeDef = {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  borderClassName?: string;
  floatClassName?: string;
};

export type UseCaseBadgeColumns = {
  left: UseCaseBadgeDef[];
  right: UseCaseBadgeDef[];
};

const badge = (
  def: UseCaseBadgeDef,
  index: number,
): ReactNode => (
  <FloatingFeatureBadge
    key={`${def.title}-${index}`}
    icon={def.icon}
    title={def.title}
    subtitle={def.subtitle}
    borderClassName={def.borderClassName}
    floatClassName={def.floatClassName}
    className="hidden lg:block"
  />
);

export const useCaseFloatingBadgesById: Record<string, UseCaseBadgeColumns> = {
  vendas: {
    left: [
      {
        icon: <Zap className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />,
        title: "IA ativa",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
      {
        icon: <ClipboardList className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />,
        title: "Lead no CRM",
        subtitle: "Funil e tags",
        borderClassName: "border-violet-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
    ],
    right: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />,
        title: "Demo agendada",
        subtitle: "Google Calendar",
        borderClassName: "border-sky-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
      {
        icon: <RefreshCw className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />,
        title: "Follow-up",
        subtitle: "Lembrete automático",
        borderClassName: "border-emerald-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
    ],
  },
  suporte: {
    left: [
      {
        icon: <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />,
        title: "Triagem IA",
        subtitle: "Coleta de contexto",
        borderClassName: "border-primary/25",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <UserPlus className="h-3.5 w-3.5 shrink-0 text-orange-500" aria-hidden />,
        title: "Escalado para humano",
        subtitle: "Histórico completo",
        borderClassName: "border-orange-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <ClipboardList className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />,
        title: "Ticket registrado",
        subtitle: "Prioridade média",
        borderClassName: "border-sky-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  imobiliario: {
    left: [
      {
        icon: <Calendar className="h-3.5 w-3.5 shrink-0 text-teal-600" aria-hidden />,
        title: "Visita agendada",
        subtitle: "Confirmação enviada",
        borderClassName: "border-teal-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Lembrete",
        subtitle: "Antes da visita",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <RefreshCw className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />,
        title: "Follow-up",
        subtitle: "Lead no funil",
        borderClassName: "border-emerald-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  clinicas: {
    left: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-rose-500" aria-hidden />,
        title: "Consulta marcada",
        subtitle: "Google Calendar",
        borderClassName: "border-rose-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Lembrete",
        subtitle: "24h e 2h antes",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <MessageSquare className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />,
        title: "Orientações",
        subtitle: "Enviadas no chat",
        borderClassName: "border-violet-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  educacao: {
    left: [
      {
        icon: <Link2 className="h-3.5 w-3.5 shrink-0 text-indigo-600" aria-hidden />,
        title: "Link de pagamento",
        subtitle: "Matrícula online",
        borderClassName: "border-indigo-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <ClipboardList className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />,
        title: "Lead no CRM",
        subtitle: "Turma março",
        borderClassName: "border-sky-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <FileText className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Certificado",
        subtitle: "Após conclusão",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  saas: {
    left: [
      {
        icon: <FileText className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />,
        title: "Guia enviado",
        subtitle: "PDF onboarding",
        borderClassName: "border-slate-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />,
        title: "Demo agendada",
        subtitle: "Convite por e-mail",
        borderClassName: "border-sky-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Lembrete",
        subtitle: "Antes da demo",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  ecommerce: {
    left: [
      {
        icon: <ShoppingBag className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Catálogo",
        subtitle: "Estoque em tempo real",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <Link2 className="h-3.5 w-3.5 shrink-0 text-blue-600" aria-hidden />,
        title: "Link enviado",
        subtitle: "Produto no chat",
        borderClassName: "border-blue-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />,
        title: "Upsell",
        subtitle: "Meia com 15% off",
        borderClassName: "border-violet-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  beleza: {
    left: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-fuchsia-600" aria-hidden />,
        title: "Horário reservado",
        subtitle: "Sexta 15h30",
        borderClassName: "border-fuchsia-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <MessageSquare className="h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />,
        title: "Orientações",
        subtitle: "Pré-procedimento",
        borderClassName: "border-violet-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Lembrete",
        subtitle: "Confirmação no dia",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  restaurantes: {
    left: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden />,
        title: "Reserva confirmada",
        subtitle: "20h · 4 pessoas",
        borderClassName: "border-red-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <FileText className="h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden />,
        title: "Cardápio PDF",
        subtitle: "Opções veganas",
        borderClassName: "border-red-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <Bell className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />,
        title: "Lembrete",
        subtitle: "No dia da reserva",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
  juridico: {
    left: [
      {
        icon: <ClipboardList className="h-3.5 w-3.5 shrink-0 text-stone-600" aria-hidden />,
        title: "Triagem feita",
        subtitle: "Dados coletados",
        borderClassName: "border-stone-200/80",
        floatClassName: "animate-cta-float -translate-y-1",
      },
    ],
    right: [
      {
        icon: <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />,
        title: "Consulta agendada",
        subtitle: "Quinta, 14h",
        borderClassName: "border-sky-200/80",
        floatClassName: "animate-cta-float-delay-1 translate-y-0.5",
      },
      {
        icon: <FileText className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />,
        title: "Documentos",
        subtitle: "Lista enviada",
        borderClassName: "border-amber-200/80",
        floatClassName: "animate-cta-float-delay-2 translate-y-1",
      },
    ],
  },
};

type UseCasePhoneWithBadgesProps = {
  scenarioId: string;
  children: ReactNode;
  className?: string;
};

/** Balões fora do telefone (flex lateral), sem sobrepor o chat. */
export function UseCasePhoneWithBadges({ scenarioId, children, className }: UseCasePhoneWithBadgesProps) {
  const columns = useCaseFloatingBadgesById[scenarioId];

  return (
    <div
      className={cn(
        "relative flex w-full max-w-full items-center justify-center gap-2 sm:gap-3 lg:gap-4",
        className,
      )}
    >
      {columns?.left.length ? (
        <div className="pointer-events-none hidden shrink-0 flex-col items-end gap-2 lg:flex">
          {columns.left.map((def, i) => badge(def, i))}
        </div>
      ) : null}
      <div className="min-w-0 shrink-0">{children}</div>
      {columns?.right.length ? (
        <div className="pointer-events-none hidden shrink-0 flex-col gap-2 lg:flex">
          {columns.right.map((def, i) => badge(def, i))}
        </div>
      ) : null}
    </div>
  );
}
